import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';

// Define types for form data
interface MySQLFormData {
  host: string;
  port: number;
  user: string;
  password: string;
  db_name: string;
}

interface MQTTFormData {
  host: string;
  port: number;
  username: string;
  password: string;
  client_id: string;
  topic: string;
}

interface AdminFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  full_name: string;
}

interface ConnectionStatus {
  success: boolean;
  message: string;
}

interface SetupStatus {
  mysql_connection: ConnectionStatus;
  database_creation: ConnectionStatus;
  mqtt_connection: ConnectionStatus;
  admin_creation: ConnectionStatus;
  env_file_updated: ConnectionStatus;
}

const Setup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formValid, setFormValid] = useState(false);

  // Form data
  const [mysqlForm, setMysqlForm] = useState<MySQLFormData>({
    host: 'localhost',
    port: 3306,
    user: 'root',
    password: 'Root2mysql!',
    db_name: 'vibration_sensors_deploy',
  });

  const [mqttForm, setMqttForm] = useState<MQTTFormData>({
    host: 'mqtt.ingecom.ch',
    port: 1883,
    username: 'test',
    password: 'test',
    client_id: 'vibration_sensor_platform',
    topic: 'dira/sensors/#',
  });

  const [adminForm, setAdminForm] = useState<AdminFormData>({
    username: 'admin',
    email: 'admin@ingecom.ch',
    password: 'adminadmin',
    confirmPassword: 'adminadmin',
    full_name: '',
  });

  // Form validation
  const validateMySQLForm = () => {
    return (
      mysqlForm.host.trim() !== '' &&
      mysqlForm.port > 0 &&
      mysqlForm.port <= 65535 &&
      mysqlForm.user.trim() !== '' &&
      mysqlForm.db_name.trim() !== ''
    );
  };

  const validateMQTTForm = () => {
    return (
      mqttForm.host.trim() !== '' &&
      mqttForm.port > 0 &&
      mqttForm.port <= 65535
    );
  };

  const validateAdminForm = () => {
    return (
      adminForm.username.trim() !== '' &&
      adminForm.email.trim() !== '' &&
      adminForm.email.includes('@') &&
      adminForm.password.trim() !== '' &&
      adminForm.password.length >= parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6') &&
      adminForm.password === adminForm.confirmPassword
    );
  };

  // Steps definitions
  const steps = [
    t('setup.steps.mysql'),
    t('setup.steps.mqtt'),
    t('setup.steps.admin'),
    t('setup.steps.summary'),
  ];

  // Handle form changes
  const handleMySQLChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setMysqlForm((prev) => ({
      ...prev,
      [name]: name === 'port' ? parseInt(value) || 0 : value,
    }));
  };

  const handleMQTTChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setMqttForm((prev) => ({
      ...prev,
      [name]: name === 'port' ? parseInt(value) || 0 : value,
    }));
  };

  const handleAdminChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setAdminForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle next step
  const handleNext = () => {
    let isValid = false;
    switch (activeStep) {
      case 0:
        isValid = validateMySQLForm();
        break;
      case 1:
        isValid = validateMQTTForm();
        break;
      case 2:
        isValid = validateAdminForm();
        break;
      case 3:
        isValid = true; // Summary page always valid
        break;
      default:
        isValid = false;
    }

    setFormValid(isValid);
    if (isValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  // Handle back button
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle password visibility toggle
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Submit setup data to backend
  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const requestData = {
        mysql: {
          host: mysqlForm.host,
          port: mysqlForm.port,
          user: mysqlForm.user,
          password: mysqlForm.password,
          db_name: mysqlForm.db_name,
        },
        mqtt: {
          host: mqttForm.host,
          port: mqttForm.port,
          username: mqttForm.username || null,
          password: mqttForm.password || null,
          client_id: mqttForm.client_id,
          topic: mqttForm.topic,
        },
        admin: {
          username: adminForm.username,
          email: adminForm.email,
          password: adminForm.password,
          full_name: adminForm.full_name || null,
        },
      };

      const response = await api.post<SetupStatus>(
        '/setup/system-setup',
        requestData
      );

      setSetupStatus(response.data);
      
      // Check if all statuses are successful to determine overall success
      const allSuccessful = Object.values(response.data).every((status) => status.success);
      
      if (allSuccessful) {
        setSetupComplete(true);
      }
    } catch (error: any) {
      console.error('Setup error:', error);
      setErrorMessage(
        error.response?.data?.detail || 
        t('setup.error')
      );
    } finally {
      setLoading(false);
    }
  };

  // Render form step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('setup.mysql.title')}
            </Typography>
            <Typography paragraph>
              {t('setup.mysql.description')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={t('setup.mysql.host')}
                  name="host"
                  value={mysqlForm.host}
                  onChange={handleMySQLChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('setup.mysql.port')}
                  name="port"
                  type="number"
                  value={mysqlForm.port}
                  onChange={handleMySQLChange}
                  required
                  margin="normal"
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.mysql.user')}
                  name="user"
                  value={mysqlForm.user}
                  onChange={handleMySQLChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel htmlFor="mysql-password">
                    {t('setup.mysql.password')}
                  </InputLabel>
                  <OutlinedInput
                    id="mysql-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={mysqlForm.password}
                    onChange={handleMySQLChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t('setup.mysql.password')}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('setup.mysql.dbName')}
                  name="db_name"
                  value={mysqlForm.db_name}
                  onChange={handleMySQLChange}
                  required
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('setup.mqtt.title')}
            </Typography>
            <Typography paragraph>
              {t('setup.mqtt.description')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={t('setup.mqtt.host')}
                  name="host"
                  value={mqttForm.host}
                  onChange={handleMQTTChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('setup.mqtt.port')}
                  name="port"
                  type="number"
                  value={mqttForm.port}
                  onChange={handleMQTTChange}
                  required
                  margin="normal"
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.mqtt.username')}
                  name="username"
                  value={mqttForm.username}
                  onChange={handleMQTTChange}
                  margin="normal"
                  helperText={t('setup.mqtt.usernameHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel htmlFor="mqtt-password">
                    {t('setup.mqtt.password')}
                  </InputLabel>
                  <OutlinedInput
                    id="mqtt-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={mqttForm.password}
                    onChange={handleMQTTChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t('setup.mqtt.password')}
                  />
                  <FormHelperText>
                    {t('setup.mqtt.passwordHelp')}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.mqtt.clientId')}
                  name="client_id"
                  value={mqttForm.client_id}
                  onChange={handleMQTTChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.mqtt.topic')}
                  name="topic"
                  value={mqttForm.topic}
                  onChange={handleMQTTChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('setup.admin.title')}
            </Typography>
            <Typography paragraph>
              {t('setup.admin.description')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.admin.username')}
                  name="username"
                  value={adminForm.username}
                  onChange={handleAdminChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('setup.admin.email')}
                  name="email"
                  type="email"
                  value={adminForm.email}
                  onChange={handleAdminChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel htmlFor="admin-password">
                    {t('setup.admin.password')}
                  </InputLabel>
                  <OutlinedInput
                    id="admin-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={adminForm.password}
                    onChange={handleAdminChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t('setup.admin.password')}
                  />
                  <FormHelperText>
                    {t('setup.admin.passwordHelp', { minLength: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6') })}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel htmlFor="admin-confirm-password">
                    {t('setup.admin.confirmPassword')}
                  </InputLabel>
                  <OutlinedInput
                    id="admin-confirm-password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={adminForm.confirmPassword}
                    onChange={handleAdminChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t('setup.admin.confirmPassword')}
                    error={adminForm.password !== adminForm.confirmPassword && adminForm.confirmPassword !== ''}
                  />
                  {adminForm.password !== adminForm.confirmPassword && adminForm.confirmPassword !== '' && (
                    <FormHelperText error>
                      {t('setup.admin.passwordsDoNotMatch')}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('setup.admin.fullName')}
                  name="full_name"
                  value={adminForm.full_name}
                  onChange={handleAdminChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('setup.summary.title')}
            </Typography>
            <Typography paragraph>
              {t('setup.summary.description')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title={t('setup.mysql.title')} />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2">
                      <strong>{t('setup.mysql.host')}:</strong> {mysqlForm.host}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mysql.port')}:</strong> {mysqlForm.port}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mysql.user')}:</strong> {mysqlForm.user}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mysql.dbName')}:</strong> {mysqlForm.db_name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title={t('setup.mqtt.title')} />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2">
                      <strong>{t('setup.mqtt.host')}:</strong> {mqttForm.host}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mqtt.port')}:</strong> {mqttForm.port}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mqtt.clientId')}:</strong> {mqttForm.client_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.mqtt.topic')}:</strong> {mqttForm.topic}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title={t('setup.admin.title')} />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2">
                      <strong>{t('setup.admin.username')}:</strong> {adminForm.username}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('setup.admin.email')}:</strong> {adminForm.email}
                    </Typography>
                    {adminForm.full_name && (
                      <Typography variant="body2">
                        <strong>{t('setup.admin.fullName')}:</strong> {adminForm.full_name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* Show setup status results if available */}
            {setupStatus && (
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  {t('setup.status.title')}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert 
                      severity={setupStatus.mysql_connection.success ? "success" : "error"}
                      sx={{ mb: 1 }}
                    >
                      {t('setup.status.mysql')}: {setupStatus.mysql_connection.message}
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert 
                      severity={setupStatus.database_creation.success ? "success" : "error"}
                      sx={{ mb: 1 }}
                    >
                      {t('setup.status.database')}: {setupStatus.database_creation.message}
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert 
                      severity={setupStatus.mqtt_connection.success ? "success" : "error"}
                      sx={{ mb: 1 }}
                    >
                      {t('setup.status.mqtt')}: {setupStatus.mqtt_connection.message}
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert 
                      severity={setupStatus.admin_creation.success ? "success" : "error"}
                      sx={{ mb: 1 }}
                    >
                      {t('setup.status.admin')}: {setupStatus.admin_creation.message}
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert 
                      severity={setupStatus.env_file_updated.success ? "success" : "error"}
                      sx={{ mb: 1 }}
                    >
                      {t('setup.status.env')}: {setupStatus.env_file_updated.message}
                    </Alert>
                  </Grid>
                </Grid>
                
                {setupComplete && (
                  <Box mt={4} textAlign="center">
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {t('setup.status.complete')}
                    </Alert>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => navigate('/login')}
                    >
                      {t('setup.status.goToLogin')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 900,
          mx: 'auto',
          p: 3,
          mb: 4,
        }}
      >
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          {t('setup.title')}
        </Typography>
        
        <Typography variant="subtitle1" align="center" color="text.secondary" paragraph>
          {t('setup.subtitle')}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ mt: 2, mb: 2 }}>
          {getStepContent(activeStep)}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            {t('common.back')}
          </Button>
          
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || setupComplete}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                ) : null}
                {t('setup.submit')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading}
              >
                {t('common.next')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Error snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert 
          onClose={() => setErrorMessage(null)} 
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Setup;